import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchHotel, fetchHotels, fetchHotelAvailability, createHotelReservation } from "@/lib/api";
import { fmtPrice, withWatermark } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { MapPin, ChevronLeft, Wifi, Car, Coffee, Snowflake, BedDouble, Settings, CheckCircle2, Loader2, CalendarDays, BadgeCheck, Users, Ruler } from "lucide-react";
import { HotelManagerLinkButton } from "@/components/HotelManagerLinkButton";
import { DepositPolicyDialog } from "@/components/DepositPolicyDialog";
import { HotelReviewsSection, Stars } from "@/components/HotelReviewsSection";
import { YangoRideButton } from "@/components/YangoRideButton";

const amenityIcons = {
  "Wi-Fi": Wifi, "Wi-Fi pro": Wifi, "Parking": Car,
  "Petit dejeuner inclus": Coffee, "Climatisation": Snowflake,
};
const pad = (n) => String(n).padStart(2, "0");

export default function HotelDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [h, setH] = useState(null);
  const [others, setOthers] = useState([]);
  const [avail, setAvail] = useState({ slots: [], config: {}, date: "" });
  const [canManage, setCanManage] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [bookDate, setBookDate] = useState(today);
  const [bookHour, setBookHour] = useState(null);
  const [bookRooms, setBookRooms] = useState(1);
  const [roomType, setRoomType] = useState("");
  const [bookName, setBookName] = useState("");
  const [bookPhone, setBookPhone] = useState("");
  const [booking, setBooking] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [showPolicy, setShowPolicy] = useState(false);
  const [payOpts, setPayOpts] = useState({ payment_method: "mobile_money", customer_country: "FR" });

  useEffect(() => {
    fetchHotel(id).then(setH).catch(() => setH(null));
    fetchHotels().then((d) => setOthers(d.filter((x) => x.id !== id).slice(0, 3)));
  }, [id]);

  useEffect(() => {
    fetchHotelAvailability(id, bookDate).then(setAvail).catch(() => {});
  }, [id, bookDate]);

  useEffect(() => {
    // Allow "Gerer" link if user.email is in our static admin (rough check)
    if (user?.email === "admin@fodindin.com" || user?.is_admin || (h && h.manager_user_id === user?.user_id)) {
      setCanManage(true);
    }
  }, [user, h]);

  const isOffPeak = (hour) => {
    const cfg = avail.config || {};
    const s = cfg.off_peak_start ?? 0;
    const e = cfg.off_peak_end ?? 6;
    return s < e ? hour >= s && hour < e : hour >= s || hour < e;
  };

  const activeSlots = useMemo(
    () => (avail.slots || []).filter((s) => s.is_active && s.available_rooms > 0),
    [avail]
  );

  const selectedSlot = activeSlots.find((s) => s.hour === bookHour) || null;
  const selectedRoom = roomType ? (h?.chambres || []).find((c) => c.nom === roomType && c.prix_nuit > 0) : null;
  const selectedPrice = selectedSlot
    ? (() => {
        const slotPrice = isOffPeak(selectedSlot.hour) ? selectedSlot.off_peak_price : selectedSlot.regular_price;
        if (!selectedRoom) return slotPrice * bookRooms;
        const base = h.price_public || h.price || selectedSlot.regular_price;
        return Math.round(selectedRoom.prix_nuit * (base ? slotPrice / base : 1)) * bookRooms;
      })()
    : null;

  const submitReservation = async () => {
    if (bookHour === null) { toast.error("Choisissez un creneau horaire."); return; }
    if (!bookName.trim()) { toast.error("Indiquez votre nom."); return; }
    if ((bookPhone || "").replace(/\D/g, "").length < 8) { toast.error("Numero de telephone invalide."); return; }
    setShowPolicy(true);
  };

  const confirmReservation = async () => {
    setBooking(true);
    try {
      const res = await createHotelReservation(id, {
        name: bookName.trim(), phone: bookPhone.trim(),
        date: bookDate, hour: bookHour, rooms: bookRooms, room_type: roomType || undefined,
        ...(payOpts.payment_method === "card" ? payOpts : {}),
        ...(payOpts.use_third_party && payOpts.payer_phone ? { payer_phone: payOpts.payer_phone } : {}),
      });
      if (res.payment?.payment_url) {
        toast.success(`Chambre bloquee 20 min (ref ${res.reservation.ref}). Redirection vers le paiement de l'acompte...`);
        window.location.href = res.payment.payment_url;
        return;
      }
      setConfirmation(res.reservation);
      toast.success(`Reservation confirmee ! Ref : ${res.reservation.ref}`);
      fetchHotelAvailability(id, bookDate).then(setAvail).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de la reservation. Reessayez.");
    } finally { setBooking(false); setShowPolicy(false); }
  };

  if (!h) return <main className="max-w-7xl mx-auto px-6 py-20" data-testid="hotel-loading">Chargement...</main>;

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="hotel-detail-page">
      <Link to="/hotelleries" className="inline-flex items-center gap-1 text-orange-600 font-semibold mb-5">
        <ChevronLeft size={16} /> Retour aux hotelleries
      </Link>

      <div className="rounded-[24px] overflow-hidden">
        <img src={withWatermark(h.image)} alt={h.name} className="w-full h-[440px] object-cover" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 mt-10">
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="badge-premium">Hotel partenaire</span>
            <span className="badge-verified">Chambres disponibles</span>
            {canManage && (
              <Link
                to={`/admin/hotels/${h.id}/disponibilites`}
                className="badge-verified inline-flex items-center gap-1"
                style={{ background: "#fff1e3", color: "#9a3412" }}
                data-testid="manage-hotel-link"
              >
                <Settings size={11} /> Gerer
              </Link>
            )}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">{h.name}</h1>
          <div className="mt-3 text-orange-600 font-extrabold text-2xl">
            {fmtPrice(h.price)} <span className="text-sm text-[var(--ink-soft)] font-medium">/ nuit</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm text-[var(--ink-soft)]">
            <MapPin size={14} /> {h.city}{h.quartier ? ` - ${h.quartier}` : ""}
            {h.rating_count > 0 && <span className="inline-flex items-center gap-1 ml-2" data-testid="hotel-rating-summary"><Stars value={h.rating_avg} /> <b>{h.rating_avg}</b> ({h.rating_count} avis verifies)</span>}
          </div>
          <div className="mt-3"><YangoRideButton hotel={h} compact /></div>

          <div className="mt-5 flex flex-wrap gap-2">
            {h.amenities.map((a) => {
              const Icon = amenityIcons[a];
              return (
                <span key={a} className="chip">
                  {Icon ? <Icon size={13} /> : null} {a}
                </span>
              );
            })}
            {h.tag && <span className="chip" style={{ color: "#16a34a", borderColor: "#bbf7d0" }}>{h.tag}</span>}
            {h.is_partner && (
              <span className="chip inline-flex items-center gap-1" style={{ color: "#92400e", borderColor: "#fcd34d", background: "#fffbeb" }} data-testid="hotel-partner-badge">
                <BadgeCheck size={13} /> Hotel partenaire
              </span>
            )}
          </div>
          <HotelManagerLinkButton hotel={h} />

          <section className="mt-8 glass-card p-7">
            <h2 className="font-bold mb-2 text-lg">Description</h2>
            <p className="leading-relaxed">{h.description}</p>
          </section>
          {(h.chambres || []).filter((c) => c.capacite).length > 0 && (
            <section className="mt-10" data-testid="hotel-room-types">
              <h2 className="text-lg md:text-xl font-extrabold flex items-center gap-2"><BedDouble size={18} className="text-orange-500" /> Nos chambres</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {h.chambres.filter((c) => c.capacite).map((c, i) => (
                  <article key={c.id || i} className={`card overflow-hidden ${roomType === c.nom ? "ring-2 ring-orange-500" : ""}`} data-testid={`room-type-${i}`}>
                    {c.image && <img src={withWatermark(c.image)} alt={c.nom} className="w-full h-40 object-cover" loading="lazy" />}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-bold">{c.nom}</div>
                        {c.prix_nuit > 0 && <div className="text-sm font-black text-orange-600 whitespace-nowrap">{fmtPrice(c.prix_nuit)}<span className="text-[10px] text-neutral-500 font-semibold"> / nuit</span></div>}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--ink-soft)]">
                        {c.lits && <span className="inline-flex items-center gap-1"><BedDouble size={12} /> {c.lits}</span>}
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {c.capacite} pers.</span>
                        {c.surface_m2 > 0 && <span className="inline-flex items-center gap-1"><Ruler size={12} /> {c.surface_m2} m²</span>}
                      </div>
                      {c.equipements?.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{c.equipements.slice(0, 6).map((e) => <span key={e} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">{e}</span>)}</div>}
                      <button type="button" onClick={() => { setRoomType(c.nom); setConfirmation(null); document.querySelector("[data-testid='reservation-form']")?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                        className={`mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-3 py-2 rounded-xl transition ${roomType === c.nom ? "bg-orange-500 text-white" : "border border-orange-300 text-orange-700 hover:bg-orange-50"}`} data-testid={`room-type-book-${i}`}>
                        <CalendarDays size={14} /> {roomType === c.nom ? "Type selectionne" : "Reserver ce type"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <HotelReviewsSection hotelId={h.id} canReply={canManage} />

          {/* 24h availability grid (public) */}
          <section className="mt-6 glass-card p-7" data-testid="public-availability">
            <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">Disponibilites en temps reel</div>
                <h2 className="font-bold text-lg">{bookDate === avail.date && avail.date ? avail.date : bookDate}</h2>
              </div>
              <div className="text-xs text-[var(--ink-soft)]">
                {avail.price_adjustment && avail.price_adjustment.pct !== 0 && (
                  <span className={`mr-2 px-2 py-0.5 rounded-full font-bold ${avail.price_adjustment.pct < 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`} data-testid="hotel-flash-price-badge">
                    {avail.price_adjustment.pct < 0 ? "Prix flash " : "Forte affluence "}{avail.price_adjustment.pct > 0 ? "+" : ""}{avail.price_adjustment.pct}% aujourd&apos;hui
                  </span>
                )}
                Heures creuses : {pad(avail.config?.off_peak_start ?? 0)}:00 - {pad(avail.config?.off_peak_end ?? 6)}:00
                {" - "}
                <b className="text-orange-600">-{avail.config?.off_peak_discount ?? 30}%</b>
              </div>
            </div>

            {activeSlots.length === 0 ? (
              <div className="text-center py-8 text-[var(--ink-soft)]">
                <BedDouble size={28} className="mx-auto mb-2 text-orange-400" />
                Aucun creneau actif pour aujourd'hui. Revenez plus tard ou contactez l'hotel directement.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="availability-grid">
                {activeSlots.map((s) => {
                  const off = isOffPeak(s.hour);
                  const price = off ? s.off_peak_price : s.regular_price;
                  const selected = bookHour === s.hour;
                  return (
                    <button
                      type="button"
                      key={s.hour}
                      onClick={() => setBookHour(s.hour)}
                      className={`text-left rounded-2xl p-3 border transition-transform hover:translate-y-[-2px] cursor-pointer ${
                        selected
                          ? "border-orange-500 ring-2 ring-orange-300 bg-orange-50"
                          : off ? "border-orange-200 bg-orange-50/60" : "border-[var(--line)] bg-white"
                      }`}
                      data-testid={`pub-slot-${s.hour}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-extrabold text-orange-600 text-[15px]">{pad(s.hour)}:00</div>
                        {selected
                          ? <CheckCircle2 size={14} className="text-orange-600" />
                          : off && <span className="text-[10px] font-bold uppercase tracking-widest text-orange-600">Off-peak</span>}
                      </div>
                      <div className="mt-1 text-[11px] text-[var(--ink-soft)]">{s.available_rooms} chambre{s.available_rooms > 1 ? "s" : ""}</div>
                      <div className="mt-1 font-bold text-[var(--ink)]">{fmtPrice(price)}</div>
                      {off && (
                        <div className="text-[11px] text-[var(--ink-soft)] line-through">{fmtPrice(s.regular_price)}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <aside className="glass-card p-7 h-fit lg:sticky lg:top-24" data-testid="hotel-pricing">
          <div className="font-bold text-lg mb-4">Politique tarifaire Fodindin</div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[var(--ink-soft)]">Tarif public</span><b>{fmtPrice(h.price_public)}</b></div>
            <div className="flex justify-between text-orange-600 font-bold"><span>Tarif heures creuses</span><span>{fmtPrice(h.price)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--ink-soft)]">Tarif forte affluence</span><b>{fmtPrice(h.price_peak)}</b></div>
            <div className="flex justify-between pt-3 border-t border-[var(--line)]">
              <span className="text-[var(--ink-soft)]">Disponibilite</span>
              <b className="text-green-600">{activeSlots.reduce((sum, s) => sum + s.available_rooms, 0)} chambres aujourd'hui</b>
            </div>
          </div>
          {confirmation ? (
            <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-4" data-testid="reservation-confirmation">
              <div className="inline-flex items-center gap-2 text-green-700 font-bold">
                <CheckCircle2 size={18} /> Reservation confirmee !
              </div>
              <div className="mt-2 text-sm space-y-1">
                <div><b>Ref :</b> <span className="font-mono">{confirmation.ref}</span></div>
                <div><b>Date :</b> {confirmation.date} a {pad(confirmation.hour)}:00</div>
                <div><b>Chambres :</b> {confirmation.rooms}</div>
                <div><b>Total :</b> <span className="text-orange-600 font-bold">{fmtPrice(confirmation.total_price)}</span></div>
              </div>
              <div className="text-xs text-[var(--ink-soft)] mt-2">
                Une confirmation SMS avec votre reference vous a ete envoyee. Presentez cette reference a la reception. Paiement sur place : Wave / Orange Money / MTN.
              </div>
              <button
                type="button"
                onClick={() => setConfirmation(null)}
                className="btn-secondary w-full mt-3 text-sm"
                data-testid="new-reservation-btn"
              >
                Nouvelle reservation
              </button>
            </div>
          ) : (
            <div className="mt-5 space-y-3" data-testid="reservation-form">
              <div className="font-bold text-sm inline-flex items-center gap-1.5">
                <CalendarDays size={15} className="text-orange-500" /> Reserver une chambre
              </div>
              {roomType && (
                <div className="flex items-center justify-between gap-2 rounded-xl bg-orange-50 border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-800" data-testid="reservation-room-type">
                  <span><BedDouble size={12} className="inline mr-1" /> Type : {roomType}</span>
                  <button type="button" onClick={() => setRoomType("")} className="underline" data-testid="reservation-room-type-clear">changer</button>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-[var(--ink-soft)]">Date</label>
                <input
                  type="date"
                  min={today}
                  value={bookDate}
                  onChange={(e) => { setBookDate(e.target.value); setBookHour(null); }}
                  className="w-full mt-1 !py-2 !px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
                  data-testid="reservation-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-[var(--ink-soft)]">Heure d&apos;arrivee</label>
                  <select
                    value={bookHour === null ? "" : bookHour}
                    onChange={(e) => setBookHour(e.target.value === "" ? null : Number(e.target.value))}
                    className="w-full mt-1 py-2 px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
                    data-testid="reservation-hour-select"
                  >
                    <option value="">Choisir...</option>
                    {activeSlots.map((s) => (
                      <option key={s.hour} value={s.hour}>
                        {pad(s.hour)}:00 — {fmtPrice(isOffPeak(s.hour) ? s.off_peak_price : s.regular_price)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[var(--ink-soft)]">Chambres</label>
                  <select
                    value={bookRooms}
                    onChange={(e) => setBookRooms(Number(e.target.value))}
                    className="w-full mt-1 py-2 px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
                    data-testid="reservation-rooms-select"
                  >
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <input
                type="text"
                placeholder="Votre nom complet"
                value={bookName}
                onChange={(e) => setBookName(e.target.value)}
                className="w-full !py-2 !px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
                data-testid="reservation-name-input"
              />
              <input
                type="tel"
                placeholder="Telephone (+225...)"
                value={bookPhone}
                onChange={(e) => setBookPhone(e.target.value)}
                className="w-full !py-2 !px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
                data-testid="reservation-phone-input"
              />
              {selectedPrice !== null && (
                <div className="flex justify-between text-sm font-bold" data-testid="reservation-total">
                  <span>Total ({bookRooms} ch.{selectedRoom ? ` ${selectedRoom.nom}` : ""} x {pad(bookHour)}:00)</span>
                  <span className="text-orange-600">{fmtPrice(selectedPrice)}</span>
                </div>
              )}
              {selectedPrice !== null && (
                <div className="text-xs text-[var(--ink-soft)]" data-testid="reservation-deposit-hint">
                  Acompte Mobile Money de 30 % (min. 1 000 FCFA) pour confirmer · solde a la reception.
                </div>
              )}
              <DepositPolicyDialog open={showPolicy} onOpenChange={setShowPolicy} total={selectedPrice || 0} onConfirm={confirmReservation} busy={booking} payOpts={payOpts} onPayOptsChange={setPayOpts} />
              <button
                type="button"
                onClick={submitReservation}
                disabled={booking || activeSlots.length === 0}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="hotel-reserve-btn"
              >
                {booking ? <Loader2 size={16} className="animate-spin" /> : <BedDouble size={16} />}
                {booking ? "Reservation..." : activeSlots.length === 0 ? "Aucun creneau disponible" : "Reserver maintenant"}
              </button>
              <div className="text-xs text-center text-[var(--ink-soft)]">Paiement sur place : Wave / Orange Money / MTN</div>
            </div>
          )}
        </aside>      </div>

      {others.length > 0 && (
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-6">Autres hotelleries</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.map((o) => (
              <Link key={o.id} to={`/hotelleries/${o.id}`} className="card">
                <img src={withWatermark(o.image)} className="w-full h-44 object-cover" alt={o.name} />
                <div className="p-4">
                  <div className="font-bold">{o.name}</div>
                  <div className="text-orange-600 font-bold mt-1">{fmtPrice(o.price)} <span className="text-xs text-[var(--ink-soft)] font-medium">/ nuit</span></div>
                  <div className="text-xs text-[var(--ink-soft)] mt-1">{o.city}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
